import sign from 'jwt-encode';
import browserTabId from './browserTabId';
import { config } from './config';
import getHash from './getHash';

class ApiError extends Error {
  public statusCode?: number;
}

type QueuedRequest<T = any> = {
  options: ApiCallOptions;
  resolve: (value: T) => void;
  reject: (error: unknown) => void;
};

const queuedRequests: QueuedRequest[] = [];

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type ApiCallOptions = {
  method?: Method;
  endpoint: string;
  payload?: any;
  urlParams?: Record<string, string>;
  flow?: 'state' | 'legacy';
  allowedStatusCodes?: number[];
  failedStatusCodes?: number[];
  onUnauthorized?: () => void;
  sequential?: boolean;
};

const API_URL = '/api/co/v3';

const LEGACY_API_URL = '/api';

const getSignatureHeader = async (jwtSecret: string, urlParams?: Record<string, string>, payload?: any) =>
  sign(
    {
      qry: urlParams ? await getHash(JSON.stringify(urlParams)) : '',
      dta: payload ? await getHash(JSON.stringify(payload)) : '',
    },
    jwtSecret
  );

const callApi = async <T = any>({ sequential, ...options }: ApiCallOptions): Promise<T> => {
  if (sequential) {
    return queueRequest(options);
  }
  const { method, endpoint, payload, urlParams, flow, allowedStatusCodes, failedStatusCodes, onUnauthorized } = options;
  const headers: Record<string, string> = {
    'X-Shop-ID': config.appId.toString(),
    'X-Browser-Tab-ID': browserTabId.get(),
    'X-Signature': await getSignatureHeader(config.session.jwtSecret, urlParams, payload),
  };
  if (config.session.token) {
    headers['X-Session'] = config.session.token;
  }
  if (config.session.correlationId) {
    headers['X-Correlation-ID'] = config.session.correlationId;
  }

  const url =
    config.apiHost +
    (flow === 'legacy' ? LEGACY_API_URL : API_URL) +
    endpoint +
    (urlParams ? `?${new URLSearchParams(urlParams).toString()}` : '');
  let fetchOptions: RequestInit = { headers, method, credentials: 'include' };
  if (payload) {
    headers['Content-Type'] = 'application/json';
    fetchOptions = { ...fetchOptions, body: JSON.stringify(payload), method: fetchOptions.method ?? 'POST' };
  }

  const response = await fetch(url, fetchOptions);

  if (onUnauthorized && response.status === 401) {
    onUnauthorized();
    return null as unknown as T;
  }

  if (
    (response.status >= 400 && !allowedStatusCodes?.includes(response.status)) ||
    failedStatusCodes?.includes(response.status)
  ) {
    const error = new ApiError();
    error.message = response.statusText;
    error.statusCode = response.status;
    try {
      const { message } = await response.json();
      if (message) {
        error.message = message;
      }
    } catch {}
    throw error;
  } else if (
    response.status === 204 ||
    (flow === 'legacy' && (method === 'DELETE' || endpoint === '/customer/password/reset'))
  ) {
    return null as unknown as T;
  } else {
    return response.json();
  }
};

const queueRequest = <T = any>(options: ApiCallOptions): Promise<T> => {
  const promise: Promise<T> = new Promise((resolve, reject) => queuedRequests.push({ options, resolve, reject }));

  if (queuedRequests.length === 1) {
    runQueuedRequests();
  }

  return promise;
};

const runQueuedRequests = async () => {
  const requestToRun = queuedRequests[0];
  try {
    const response = await callApi(requestToRun.options);
    requestToRun.resolve(response);
  } catch (error) {
    requestToRun.reject(error);
  }

  queuedRequests.shift();

  if (queuedRequests.length > 0) {
    runQueuedRequests();
  }
};

export default callApi;
